import { useMemo } from 'react';
import type { DateFormat, FormatDateOptions } from './useFormattedDate';
import { useFormattedDate } from './useFormattedDate';

export type { DateFormat, FormatDateOptions } from './useFormattedDate';

export type EventDateInfo = Readonly<{
  timezone?: string | null;
  finalizedDate?: Readonly<{
    milliseconds?: number | null;
    dateString?: string | null;
    timestamp?: string | null;
    timezone?: string | null;
  }> | null;
  tentativeDate?: Readonly<{
    milliseconds?: number | null;
    dateString?: string | null;
    timestamp?: string | null;
    timezone?: string | null;
  }> | null;
}>;

export type FormatEventDateOptions = Readonly<{
  useTentativeFallback?: boolean;
}>;

/**
 * Hook for formatting event dates with consistent locale and timezone handling.
 *
 * @param eventInfo - Event info containing timezone and date fields
 * @param format - Date format to use (from DateFormat type)
 * @param options - Formatting options
 * @returns Formatted date string or undefined if no date available
 *
 * @example
 * // Guest side (only shows finalized dates)
 * const eventDate = useFormatEventDate(eventInfo, 'MMMM do, yyyy');
 *
 * @example
 * // Admin side (shows finalized or tentative dates)
 * const eventDate = useFormatEventDate(eventInfo, 'ddMMMyyyy', { useTentativeFallback: true });
 */
export const useFormatEventDate = (eventInfo: EventDateInfo | null | undefined, format: DateFormat, options: FormatEventDateOptions = {}): string | undefined => {
  const { useTentativeFallback = false } = options;

  // Select which date to use based on options
  const selectedDate = useMemo(() => {
    if (!eventInfo) return null;

    // Prefer finalizedDate
    if (eventInfo.finalizedDate?.milliseconds || eventInfo.finalizedDate?.dateString || eventInfo.finalizedDate?.timestamp) {
      return eventInfo.finalizedDate;
    }

    // Fallback to tentativeDate only if useTentativeFallback is true
    if (useTentativeFallback && (eventInfo.tentativeDate?.milliseconds || eventInfo.tentativeDate?.dateString || eventInfo.tentativeDate?.timestamp)) {
      return eventInfo.tentativeDate;
    }

    return null;
  }, [eventInfo, useTentativeFallback]);

  // Determine timezone to use (prefer eventInfo.timezone, fallback to date.timezone)
  const timezone = useMemo(() => {
    const trimmedEventTz = eventInfo?.timezone?.trim();
    const trimmedDateTz = selectedDate?.timezone?.trim();
    return trimmedEventTz || trimmedDateTz || null;
  }, [eventInfo?.timezone, selectedDate?.timezone]);

  // Determine date input (prefer milliseconds, then timestamp, then dateString)
  const dateInput = useMemo(() => {
    if (!selectedDate) return null;

    if (selectedDate.milliseconds) {
      return selectedDate.milliseconds;
    }

    if (selectedDate.timestamp) {
      return selectedDate.timestamp;
    }

    if (selectedDate.dateString) {
      return selectedDate.dateString;
    }

    return null;
  }, [selectedDate]);

  // Format options for useFormattedDate
  const formatOptions: FormatDateOptions = useMemo(() => {
    return {
      ...(timezone && { timezone }),
    };
  }, [timezone]);

  return useFormattedDate(typeof dateInput === 'number' ? new Date(dateInput) : dateInput, format, formatOptions);
};
